import { onDisconnect, onValue, push, ref, remove, runTransaction, serverTimestamp, set, update } from "firebase/database";
import { realtimeDatabase } from "../database/firebase";

/** Firebase RTDB keys cannot contain . # $ [ ] / */
export function sanitizeFirebaseKey(value) {
  return String(value ?? "unknown").replace(/[.#$/[\]]/g, "_");
}

/** Same org + session → same RTDB node. */
export function buildFirebaseSessionId(organizationId, sessionId) {
  return `${sanitizeFirebaseKey(organizationId || "default")}__${sanitizeFirebaseKey(sessionId)}`;
}

/**
 * Live Audience mode (PRD 2.2 / 7.7), all under the session's existing RTDB node:
 *
 *   session/{fbSessionId}/crossroadsLive/state                     host-owned game state
 *   session/{fbSessionId}/crossroadsLive/participants/{pid}         { name, online, joinedAt }
 *   session/{fbSessionId}/crossroadsLive/votes/{runId}/{qid}/{pid}  'A' | 'B' (write-once)
 *
 * state = { runId, phase, index, questionId, total, votingStartsAt, votingEndsAt, updatedAt }
 * (votingStartsAt: when the countdown begins — after players' boards reach the crossroads)
 * phase: lobby → intro → travel → voting → closed → reveal → learning → merging → … → complete
 */

const liveRoot = (fbSessionId) => `session/${fbSessionId}/crossroadsLive`;

export function newRunId() {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function subscribeLiveState(fbSessionId, onData) {
  if (!fbSessionId) return () => {};
  return onValue(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/state`), (snap) => onData(snap.val() || null));
}

export function setLiveState(fbSessionId, patch) {
  return update(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/state`), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export function subscribeParticipants(fbSessionId, onData) {
  if (!fbSessionId) return () => {};
  return onValue(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/participants`), (snap) => onData(snap.val() || {}));
}

/**
 * Presence (standard Firebase pattern): each open game screen adds its own entry under
 * participants/{pid}/connections, removed when the screen closes, the player leaves the game page,
 * or the connection drops — and added again after a reconnect. A player counts as online while
 * they have at least one connection, so rejoining, refreshing or a second tab never counts twice
 * and a player who left is never left "online". Returns leave(), to call when the screen closes.
 */
export function joinLive(fbSessionId, participantId, { name, userId = null }) {
  const pPath = `${liveRoot(fbSessionId)}/participants/${sanitizeFirebaseKey(participantId)}`;
  const pRef = ref(realtimeDatabase, pPath);
  const connRef = push(ref(realtimeDatabase, `${pPath}/connections`));
  let active = true;
  update(pRef, {
    name: String(name || "Guest").slice(0, 60),
    userId: userId ? String(userId) : null,
    online: null, // old single flag: no longer used (it could be left stale)
    lastSeen: serverTimestamp(),
  }).catch(() => {});
  // joinedAt only on first join
  runTransaction(ref(realtimeDatabase, `${pPath}/joinedAt`), (cur) => cur || Date.now()).catch(() => {});
  const stop = onValue(ref(realtimeDatabase, ".info/connected"), (snap) => {
    if (snap.val() !== true || !active) return;
    onDisconnect(connRef)
      .remove()
      .then(() => active && set(connRef, serverTimestamp()))
      .catch((err) => console.warn("Live presence not registered:", err?.message || err));
  });
  return () => {
    active = false;
    stop();
    onDisconnect(connRef).cancel().catch(() => {});
    remove(connRef).catch(() => {});
  };
}

/** Online = at least one open, connected game screen. */
export function isOnline(participant) {
  const conns = participant?.connections;
  return Boolean(conns && typeof conns === "object" && Object.keys(conns).length);
}

/** One vote per participant per crossroads: a transaction that never overwrites. */
export async function castVote(fbSessionId, runId, questionId, participantId, choice) {
  const vRef = ref(
    realtimeDatabase,
    `${liveRoot(fbSessionId)}/votes/${sanitizeFirebaseKey(runId)}/${sanitizeFirebaseKey(questionId)}/${sanitizeFirebaseKey(participantId)}`
  );
  const result = await runTransaction(vRef, (current) => (current ? undefined : choice));
  return { accepted: result.committed, choice: result.snapshot.val() };
}

/** All of a run's votes — used by participants to rebuild their own score after a refresh. */
export function subscribeRunVotes(fbSessionId, runId, onData) {
  if (!fbSessionId || !runId) return () => {};
  return onValue(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/votes/${sanitizeFirebaseKey(runId)}`), (snap) =>
    onData(snap.val() || {})
  );
}

export function countVotes(votesMap) {
  let a = 0;
  let b = 0;
  Object.values(votesMap || {}).forEach((v) => {
    if (v === "A") a += 1;
    else if (v === "B") b += 1;
  });
  return { a, b, total: a + b };
}

/**
 * Close voting once its deadline has passed — run by every connected screen, so a timed vote
 * closes even when no host is connected. It only changes this exact voting round (same run,
 * crossroads and deadline), so it can never close a newer round, and concurrent calls are safe.
 */
export async function closeVotingIfExpired(fbSessionId, { runId, index, votingEndsAt }, serverNow) {
  if (!fbSessionId || !votingEndsAt || serverNow < votingEndsAt) return false;
  const result = await runTransaction(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/state`), (cur) => {
    if (!cur || cur.phase !== "voting" || cur.runId !== runId || cur.index !== index || cur.votingEndsAt !== votingEndsAt) {
      return undefined; // someone else moved the game on — leave it
    }
    return { ...cur, phase: "closed", votingEndsAt: null, updatedAt: serverNow };
  });
  return result.committed;
}

/** Start a fresh run: clears the previous run's votes but keeps who is in the room. */
export async function resetLiveRun(fbSessionId, runId, total) {
  await set(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/votes`), null);
  await set(ref(realtimeDatabase, `${liveRoot(fbSessionId)}/state`), {
    runId,
    phase: "lobby",
    index: 0,
    questionId: null,
    total,
    votingEndsAt: null,
    updatedAt: serverTimestamp(),
  });
}

/** Server clock offset so every device counts down to the same moment. */
export function subscribeServerOffset(onOffset) {
  return onValue(ref(realtimeDatabase, ".info/serverTimeOffset"), (snap) => onOffset(Number(snap.val()) || 0));
}

export function onlineCount(participants) {
  return Object.values(participants || {}).filter(isOnline).length;
}

/**
 * Live channel for one game session: the host's Live Control and that session's players share
 * it; another session's host can never move these players (or see their votes).
 */
export function liveChannelFor(organizationId, sessionId) {
  return organizationId && sessionId ? buildFirebaseSessionId(organizationId, sessionId) : null;
}

/**
 * Votes are keyed by crossroads position, not database id — sessions with their own copy of
 * the content have different ids for the same crossroads.
 */
export const voteKey = (index) => `q${index}`;
